import {
  lookalikeFileOf,
  type LookalikeKeys,
  lookalikeKeys,
  lookalikeNeeds,
  type LookalikePart,
  lookalikePairs,
  type LookalikeShard,
  lookalikeShard,
  LookalikeSheet,
} from "./lookalike-sheet.js";
import { type PartState, PartStates } from "./part-state.js";
import { SheetShards } from "./sheet-shards.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { sheetOrder } from "./worksheet-seed.js";
import { sheetCharacters } from "./worksheet-words.js";

/**
 * The look-alike characters worksheet generator: the 形近字 sheet as a kind
 * of sheet `sheet-tool.ts` draws.
 */

/** The most pairs one sheet takes, which is a few pages and its answers. */
export const pairLimit = 40;

/** The parts, in the order they print, and whether each prints by default. */
export const lookalikeStates = new PartStates<LookalikePart>([
  ["choose", true],
  ["which", true],
  ["key", true],
]);

/** The look-alike sheet, drawn from the items of the characters it pairs. */
export function lookalikeSheetKind(
  data: SheetShards<LookalikeKeys, LookalikeShard>,
): SheetKind<PartState<LookalikePart>> {
  return {
    read: (hash) => lookalikeStates.read(hash),
    fragment: (state) => lookalikeStates.fragment(state),
    nothingAsked: (state) =>
      state.choose || state.which
        ? undefined
        : "Tick at least one exercise under Include.",
    title: (state, list) =>
      sheetTitle(state, list, {
        typed: "Look-alike characters",
        saved: "Look-alike characters from my saved words",
      }),

    draw: async ({ state, words, labels }) => {
      const characters = sheetCharacters(words, pairLimit * 4).map(
        (character) => character.character,
      );
      const keys = await data.keys();
      const items =
        keys === undefined
          ? undefined
          : await data.items(lookalikeNeeds(keys, characters, pairLimit));
      if (keys === undefined || items === undefined) {
        return "Could not load the worksheet data. Try again in a moment.";
      }
      const { pairs, unpaired } = lookalikePairs(
        lookalikeFileOf(keys, items),
        characters,
        pairLimit,
      );
      const left =
        unpaired.length === 0
          ? ""
          : ` No look-alike pair on the list for ${unpaired.slice(0, 20).join(" ")}${unpaired.length > 20 ? " and others" : ""}.`;
      if (pairs.length === 0) {
        return `None of these characters has a look-alike pair on the list.${left}`;
      }

      const library = new GlyphLibrary({ document });
      const pages = new LookalikeSheet({ library }).pages(
        state,
        sheetOrder(pairs, state),
        labels,
      );

      return {
        nodes: [library.element, ...pages],
        status: `${counted(pairs.length, "pair", "pairs")} on ${counted(pages.length, "page", "pages")}.${left}`,
      };
    },
  };
}

/** Start the look-alike worksheet generator on its container. */
export function startLookalikeWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  const data = new SheetShards({
    fetch: properties.fetch,
    address: container.dataset["cbData"] ?? "keys.json",
    readKeys: lookalikeKeys,
    readItem: lookalikeShard,
    limit: concurrency,
  });
  startSheetTool(container, lookalikeSheetKind(data), properties);
}
